import { cn } from '@lemonenergy/lemonpie-3'
import type {
  HeadersFunction,
  LoaderFunctionArgs,
  MetaFunction,
} from '@remix-run/node'
import { json } from '@remix-run/node'
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useLocation,
} from '@remix-run/react'
import { withSentry } from '@sentry/remix'

import { ToastProvider } from './components/Toast'
import usePosthogPageview from './hooks/usePosthogPageview'
import { identityCookie } from './identityCookie'
import { POSTHOG_CLIENT_KEY } from './res/constants/env.server'
import withCommonMetaTags from './utils/commonMetaTags.utils'

import stylesheet from '~/tailwind.css?url'

export const shouldRevalidate = () => false

export const meta: MetaFunction = () =>
  withCommonMetaTags({ title: 'Pé de Limão' })

export const links = () => {
  return [
    { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
    { rel: 'preconnect', href: 'https://fonts.gstatic.com' },
    {
      href: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap',
      rel: 'stylesheet',
    },
    {
      rel: 'icon',
      href: 'https://energialemon.com.br/favicon-64x64.png',
    },
    {
      rel: 'preconnect',
      href: 'https://accounts.google.com',
    },
    { rel: 'stylesheet', href: stylesheet },
  ]
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
  return {
    'Cache-Control': loaderHeaders.get('Cache-Control') ?? '',
  }
}

export const loader = async ({
  context: { userIdentityId, cacheUserIdentityId },
}: LoaderFunctionArgs) => {
  const cacheStrategy = cacheUserIdentityId
    ? 'max-age=86400, s-maxage=604800'
    : 'max-age=60, private'
  return json(
    {
      APP_VERSION: process.env.APP_VERSION || '0.0.0',
      ENABLE_MOCKED_SERVICE_WORKER:
        process.env.NODE_ENV !== 'production' &&
        JSON.parse(process.env.ENABLE_MOCKS || 'false'),
      COGNITO_BASE_URL: process.env.COGNITO_BASE_URL,
      SENTRY_DSN: process.env.SENTRY_DSN,
      SENTRY_ENVIRONMENT: process.env.STAGE || 'dev',
      STAGE: process.env.STAGE,
      POSTHOG_CLIENT_KEY: POSTHOG_CLIENT_KEY,
      USER_IDENTITY_ID: userIdentityId,
    },
    {
      headers: {
        'Set-Cookie': await identityCookie.serialize(userIdentityId),
        'Cache-Control': cacheStrategy,
      },
    },
  )
}

const noOverflowPaths = ['/upload-contas']

function App() {
  const location = useLocation()
  usePosthogPageview()
  const loaderData = useLoaderData<typeof loader>()

  const isNoOverflowPath = noOverflowPaths.some((path) =>
    location.pathname.startsWith(path),
  )

  const bodyOverflowClass = isNoOverflowPath
    ? 'overflow-hidden'
    : 'overflow-y-auto'
  const classes = 'm-0 flex flex-col flex-grow flex-shrink-0 basis-auto bg-soft'

  return (
    <html lang="pt-br" className={cn(classes, 'min-h-screen scroll-smooth')}>
      <head>
        <Meta />
        <Links />

        <script
          dangerouslySetInnerHTML={{
            __html: `
                window.APP_VERSION = "${loaderData.APP_VERSION}"
                window.ENABLE_MOCKED_SERVICE_WORKER = ${loaderData.ENABLE_MOCKED_SERVICE_WORKER} 
                window.COGNITO_BASE_URL = "${loaderData.COGNITO_BASE_URL}"
                window.SENTRY_DSN = "${loaderData.SENTRY_DSN}"
                window.SENTRY_ENVIRONMENT = "${loaderData.SENTRY_ENVIRONMENT}"
                window.env = ${JSON.stringify({
                  STAGE: loaderData.STAGE,
                  POSTHOG_CLIENT_KEY: loaderData.POSTHOG_CLIENT_KEY,
                  USER_IDENTITY_ID: loaderData?.USER_IDENTITY_ID,
                })}
              `,
          }}
        />
      </head>
      <body className={cn(classes, `min-h-full ${bodyOverflowClass}`)}>
        <ToastProvider>
          <Outlet />
        </ToastProvider>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  )
}

const bootApp = () => {
  if (typeof window === 'undefined') {
    return process.env.SENTRY_DSN ? withSentry(App) : App
  }

  return window.SENTRY_DSN ? withSentry(App) : App
}

export default bootApp()
